// src/pages/ServiceExpenseLedger/components/ServiceExpenseLedgerTable.js
import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import _ from "lodash";

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ServiceExpenseLedgerTable = ({ printMode = false }) => {
  const { list, loading } = useSelector((state) => state.serviceExpenseLedger);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const ledger = list?.ledger || [];
  const openingBalance = list?.opening_balance || 0;
  const closingBalance = list?.closing_balance || 0;

  const debitTotal = _.sumBy(ledger, (r) => Number(r.debit_amount) || 0);
  const creditTotal = _.sumBy(ledger, (r) => Number(r.credit_amount) || 0);

  const ledgerWithSeq = ledger.map((r, i) => ({ ...r, seq: i + 1 }));

  const tableData = [
    { id: "opening", rowType: "opening", balance: openingBalance },
    ...ledgerWithSeq.map((row, index) => ({
      ...row,
      id: row.id || `ledger-${index}`,
      rowType: "ledger",
    })),
    {
      id: "totals",
      rowType: "totals",
      debit_total: debitTotal,
      credit_total: creditTotal,
    },
    { id: "closing", rowType: "closing", balance: closingBalance },
  ];

  const columns = [
    {
      title: "No.",
      key: "no",
      width: 60,
      render: (_, record) => (record.rowType === "ledger" ? record.seq : ""),
    },
    {
      title: "Date",
      dataIndex: "created_date",
      key: "date",
      render: (val, record) => {
        if (record.rowType === "opening")
          return {
            children: "Opening Balance :",
            props: { colSpan: 4, style: { textAlign: "right", fontWeight: 700 } },
          };
        if (record.rowType === "closing")
          return {
            children: "Closing Balance :",
            props: { colSpan: 4, style: { textAlign: "right", fontWeight: 700 } },
          };
        if (record.rowType === "totals")
          return {
            children: "Grand Total :",
            props: { colSpan: 4, style: { textAlign: "right", fontWeight: 700 } },
          };
        return val ? dayjs(val).format("DD-MMM-YYYY") : "";
      },
    },
    {
      title: "Particular",
      dataIndex: "particular",
      key: "particular",
      render: (val, record) =>
        record.rowType !== "ledger"
          ? { children: null, props: { colSpan: 0 } }
          : val,
    },
    {
      title: "VCH No",
      dataIndex: "vch_no",
      key: "vch_no",
      render: (val, record) =>
        record.rowType !== "ledger"
          ? { children: null, props: { colSpan: 0 } }
          : val,
    },
    {
      title: "VCH Type",
      dataIndex: "vch_type",
      key: "vch_type",
      render: (val, record) =>
        record.rowType !== "ledger"
          ? { children: null, props: { colSpan: 0 } }
          : val,
    },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      key: "debit",
      align: "right",
      render: (val, record) =>
        record.rowType === "totals"
          ? <strong>{fmt(record.debit_total)}</strong>
          : record.rowType === "ledger"
          ? fmt(val)
          : "",
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      key: "credit",
      align: "right",
      render: (val, record) =>
        record.rowType === "totals"
          ? <strong>{fmt(record.credit_total)}</strong>
          : record.rowType === "ledger"
          ? fmt(val)
          : "",
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      render: (val, record) =>
        record.rowType === "opening" || record.rowType === "closing"
          ? <strong>{fmt(record.balance)}</strong>
          : record.rowType === "ledger"
          ? fmt(val)
          : "",
    },
  ];

  if (printMode) {
  return (
    <table className="ledger-print-table">
      <colgroup>
        <col style={{ width: "4%" }} />
        <col style={{ width: "11%" }} />
        <col style={{ width: "30%" }} />
        <col style={{ width: "11%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
      </colgroup>

      <tbody>
        <tr className="ledger-print-heading">
          <td>No.</td>
          <td>Date</td>
          <td>Particular</td>
          <td>VCH No</td>
          <td>VCH Type</td>
          <td style={{ textAlign: "right" }}>Debit</td>
          <td style={{ textAlign: "right" }}>Credit</td>
          <td style={{ textAlign: "right" }}>Balance</td>
        </tr>

        <tr className="ledger-opening-row">
          <td colSpan={7} style={{ textAlign: "right" }}>
            Opening Balance :
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(openingBalance)}
          </td>
        </tr>

        {ledgerWithSeq.map((row) => (
          <tr key={row.id || row.seq}>
            <td>{row.seq}</td>

            <td>
              {row.created_date
                ? dayjs(row.created_date).format("DD-MMM-YYYY")
                : ""}
            </td>

            <td>{row.particular}</td>

            <td>{row.vch_no}</td>

            <td>{row.vch_type}</td>

            <td style={{ textAlign: "right" }}>
              {fmt(row.debit_amount)}
            </td>

            <td style={{ textAlign: "right" }}>
              {fmt(row.credit_amount)}
            </td>

            <td style={{ textAlign: "right" }}>
              {fmt(row.balance)}
            </td>
          </tr>
        ))}

        <tr className="ledger-total-row">
          <td colSpan={5} style={{ textAlign: "right" }}>
            Grand Total :
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(debitTotal)}
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(creditTotal)}
          </td>

          <td />
        </tr>

        <tr className="ledger-closing-row">
          <td colSpan={7} style={{ textAlign: "right" }}>
            Closing Balance :
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(closingBalance)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={tableData}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      size={printMode ? "small" : "middle"}
      scroll={printMode ? undefined : { x: "max-content" }}
    />
  );
};

export default ServiceExpenseLedgerTable;
